// =============================================================================
// SetPassword.js  (A5 – "Passwort festlegen" per Einmal-Token)
// -----------------------------------------------------------------------------
// Ermoeglicht das Festlegen eines neuen Passworts anhand des per E-Mail
// zugesandten Einmal-Tokens (?token=...). Gesteuert ueber FEATURES.passwordReset.
// =============================================================================

import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Alert, Button, Form } from "react-bootstrap";
import UserService from "../services/user.service";
import FEATURES from "../config/features";

const MIN_PW_LEN = 10;

const isStrong = (pw) =>
  typeof pw === "string" && pw.length >= MIN_PW_LEN && /[A-Za-z]/.test(pw) && /[0-9]/.test(pw);

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [variant, setVariant] = useState("warning");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!FEATURES.passwordReset) {
    return (
      <div className="container myn-signup">
        <h1 className="myn-signup__title">Passwort festlegen</h1>
        <Alert variant="warning">Diese Funktion ist derzeit nicht verfügbar.</Alert>
        <Button as={Link} to="/login">Zur Anmeldung</Button>
      </div>
    );
  }

  const tokenMissing = token.length < 32;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    if (password !== confirm) {
      setVariant("warning");
      setMessage("Die beiden Passwörter stimmen nicht überein.");
      return;
    }
    if (!isStrong(password)) {
      setVariant("warning");
      setMessage(
        `Das Passwort muss mindestens ${MIN_PW_LEN} Zeichen lang sein und Buchstaben sowie Ziffern enthalten.`
      );
      return;
    }
    setLoading(true);
    try {
      const response = await UserService.setPasswordWithToken(token, password);
      setVariant("success");
      setDone(true);
      setMessage(
        (response.data && response.data.message) ||
          "Ihr Passwort wurde festgelegt. Sie können sich jetzt anmelden."
      );
    } catch (error) {
      const data = error && error.response ? error.response.data : null;
      setVariant("danger");
      setMessage((data && data.message) || "Das Passwort konnte nicht festgelegt werden.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container myn-signup">
      <h1 className="myn-signup__title">Passwort festlegen</h1>
      {tokenMissing && (
        <Alert variant="warning">
          Der Link ist unvollständig. Bitte fordern Sie einen neuen Link an.
        </Alert>
      )}
      {message && <Alert variant={variant}>{message}</Alert>}

      {done ? (
        <Button as={Link} to="/login">Zur Anmeldung</Button>
      ) : (
        !tokenMissing && (
          <Form onSubmit={handleSubmit} className="mt-3">
            <Form.Group controlId="newPassword">
              <Form.Label>Neues Passwort</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="new-password"
                minLength={MIN_PW_LEN}
              />
            </Form.Group>
            <Form.Group controlId="confirmPassword" className="mt-3">
              <Form.Label>Passwort wiederholen</Form.Label>
              <Form.Control
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                required
                autoComplete="new-password"
                minLength={MIN_PW_LEN}
              />
            </Form.Group>
            <Form.Text className="text-muted">
              Mindestens {MIN_PW_LEN} Zeichen, davon Buchstaben und Ziffern.
            </Form.Text>
            <div className="mt-3">
              <Button type="submit" disabled={loading}>
                {loading ? "Wird gespeichert …" : "Passwort festlegen"}
              </Button>
            </div>
          </Form>
        )
      )}

      {tokenMissing && (
        <div className="mt-4">
          <Button as={Link} to="/passwort-vergessen" variant="link">
            Neuen Link anfordern
          </Button>
        </div>
      )}
    </div>
  );
};

export default SetPassword;
